import { Editor } from "@monaco-editor/react";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import cron from "cron-validate";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { SectionHeader } from "@/components/ui";
import { STATUS_COLORS } from "@/theme/theme";
import { GH_ACTION_YAML, GL_ACTION_YAML } from "../components/configuration";
import { DataGrid } from "../components/data-grid";
import GraphVisualization from "../components/graph-visualization.tsx";
import { PageHeader } from "../components/page-header";
import { suiteRunColumns } from "../components/suite-runs-table.tsx";
import { TestSplitSelect } from "../components/test-split-select.tsx";
import { type TestActionColumnProps, testColumns } from "../components/tests-table.tsx";
import { SUGGESTED_TAGS } from "../constants/tags";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import { useAvailableTags } from "../hooks/use-tags";
import {
  type AiRequest,
  type AiResponse,
  type Graph,
  type GraphEdge,
  type GraphNode,
  type RunSuiteByIdParams,
  TEST_SUITE_FAILURE_STRATEGY_LIST,
  type Test,
  type TestSuite,
  type TestSuiteDetails,
  type TestSuiteRun,
  type TestSuiteSchema,
} from "../types.ts";
import { getTagStyle } from "../utils/tag-styles";
import { humanizeText } from "../utils/text.ts";

export default function TestSuiteDetailsPage() {
  const [addTestOpen, setAddTestOpen] = useState(false);
  const [addRuleOpen, setAddRuleOpen] = useState(false);

  const [rule, setRule] = useState<{
    left: string | null;
    right: string | null;
  }>({
    left: null,
    right: null,
  });

  const [ciOpen, setCiOpen] = useState(false);

  interface TestSuiteDetailsTabs {
    tab?: "details" | "tests" | "graph" | "runs";
  }

  const { params, setParam } = useTypedQueryParams<TestSuiteDetailsTabs>();
  const tab = params.tab ?? "details";

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setParam("tab", newValue as TestSuiteDetailsTabs["tab"]);
  };

  const [suiteTests, setSuiteTests] = useState<Test[]>([]);

  // Details tab state
  const [detailsData, setDetailsData] = useState<TestSuiteSchema | null>(null);
  const [aiText, setAiText] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();

  const { testSuiteId } = useParams();

  // AI mutation for CRON generation
  const {
    mutate: ai,
    data: aiData,
    isPending: isAiProcessing,
    reset,
  } = useApiMutation<AiRequest, AiResponse>("POST", "/ai/response");

  // Tag selection functionality
  const { data: availableTagsData } = useAvailableTags();
  const availableTags = availableTagsData?.tags || [];

  // Fetch suite runs for this test suite
  const { data: suiteRuns, isLoading: isLoadingSuiteRuns } = useApiQuery<TestSuiteRun[]>(
    `/suite-runs?test_suite_id=${testSuiteId}`,
    {
      enabled: !!testSuiteId && tab === "runs",
    },
  );

  const ghCode = useMemo(() => {
    return GH_ACTION_YAML.replace("<suite_id>", testSuiteId ?? "");
  }, [testSuiteId]);

  const glCode = useMemo(() => {
    return GL_ACTION_YAML.replace("<suite_id>", testSuiteId ?? "");
  }, [testSuiteId]);

  const {
    data: suite,
    isLoading: isLoadingSuite,
    refetch: refetchSuites,
  } = useApiQuery<TestSuiteDetails>(`/test-suites/${testSuiteId}`, {
    enabled: !!testSuiteId,
    refetchInterval: false,
  });

  // Initialize details data when suite loads
  useEffect(() => {
    if (suite) {
      setDetailsData({
        name: suite.name,
        description: suite.description,
        schedule: suite.schedule,
        schedule_enabled: suite.schedule_enabled,
        test_ids: suite.test_ids,
        webhook_ids: suite.webhook_ids || [],
        slack_channel_override: suite.slack_channel_override,
        failure_strategy: suite.failure_strategy,
        max_concurrent_runs: suite.max_concurrent_runs ?? 5,
        tag_filters: suite.tag_filters || [],
        test_dependency_graph: suite.test_dependency_graph || {},
        test_dependency_graph_conditions: suite.test_dependency_graph_conditions || {},
      });
    }
  }, [suite]);

  // Handle AI CRON generation response
  useEffect(() => {
    if (aiData?.resp) {
      if (!cron(aiData.resp).isValid()) {
        enqueueSnackbar(aiData.resp, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        return;
      }

      setAiText(null);

      setDetailsData((prev) =>
        prev
          ? {
              ...prev,
              schedule: aiData.resp,
            }
          : null,
      );

      enqueueSnackbar("CRON expression generated successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      reset();
    }
  }, [aiData, enqueueSnackbar, reset]);

  // Combine available tags with suggested tags, removing duplicates
  const allSuggestedTags = [...new Set([...availableTags, ...SUGGESTED_TAGS])];

  useEffect(() => {
    if (suite) {
      setSuiteTests(suite.tests);
    }
  }, [suite]);

  const { data: tests } = useApiQuery<Test[]>("/tests");

  const recursiveTestDep = useCallback((testId: string, allTests: Test[]): Test[] => {
    const test = allTests.find((t) => t.id === testId);
    if (!test || !test.test_dependency_ids || test.test_dependency_ids.length === 0) {
      return test ? [test] : [];
    }

    const dependencies = test.test_dependency_ids.flatMap((depId) =>
      recursiveTestDep(depId, allTests),
    );

    return [test, ...dependencies];
  }, []);

  const graph: Graph = useMemo(() => {
    if (!tests || !suite) return { nodes: [], edges: [] };

    // Use Set for O(1) lookups instead of Array.includes() (React best practice)
    const testIdSet = new Set(suite.test_ids);

    // Overwrite if the suite has a test dependency graph
    if (suite.test_dependency_graph) {
      return {
        nodes: Object.values(suite.test_dependency_graph).flatMap((test_ids) =>
          test_ids.map(
            (testId) =>
              ({
                id: testId,
                label: tests.find((t) => t.id === testId)?.name ?? "Unknown",
                type: testIdSet.has(testId) ? "test" : "dependency",
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              }) as any,
          ),
        ) as GraphNode[],
        edges: Object.entries(suite.test_dependency_graph)
          .filter(([, deps]) => deps && deps.length > 0)
          .flatMap(([testId, deps]) =>
            deps.map((depId) => ({
              target: depId,
              source: testId,
            })),
          ) as GraphEdge[],
      };
    }

    const suiteTests = suite.test_ids.flatMap((testId) => recursiveTestDep(testId, tests));

    const nodes = suiteTests.map(
      (test) =>
        ({
          id: test.id,
          label: test.name,
          type: testIdSet.has(test.id) ? "test" : "dependency",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as any as GraphNode,
    );

    const edges = suiteTests.flatMap((test) =>
      test.test_dependency_ids?.map((dep: string) => ({
        source: dep,
        target: test.id,
      })),
    ) as GraphEdge[];

    return { nodes, edges };
  }, [tests, suite, recursiveTestDep]);

  console.log({
    dep: suite?.test_dependency_graph,
    graph,
  });

  const { mutate: updateSuite, isPending: isUpdatingSuite } = useApiMutation<
    TestSuiteSchema,
    TestSuite
  >("PUT", `/test-suites/${testSuiteId}`, {
    onSuccess: () => {
      enqueueSnackbar("Test suite updated!", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      refetchSuites();

      setRule({ left: null, right: null });
      setAddTestOpen(false);
    },
  });

  // Details tab form handlers
  const handleDetailsChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setDetailsData((prev) =>
      prev
        ? {
            ...prev,
            [name]: value,
          }
        : null,
    );
  };

  const handleScheduleEnabledChange = (event: SelectChangeEvent) => {
    setDetailsData((prev) =>
      prev
        ? {
            ...prev,
            schedule_enabled: event.target.value === "on",
          }
        : null,
    );
  };

  const handleSaveDetails = () => {
    if (detailsData) {
      updateSuite(detailsData);
    }
  };

  const { mutate: runSuite, isPending: isRunningSuite } = useApiMutation<void, RunSuiteByIdParams>(
    "POST",
    `/test-suites/${testSuiteId}/run`,
    {
      onSuccess: (resp) => {
        enqueueSnackbar(
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Test suite run started successfully!</Typography>
            <IconButton
              aria-label="View suite run"
              onClick={() => {
                nav(`/suite-runs/${resp.run_id}`);
              }}
              size="small"
              color="inherit"
            >
              <OpenInNewIcon htmlColor="white" />
            </IconButton>
          </Stack>,
          {
            variant: "success",
            // For the AI agent to be able to read the message
            autoHideDuration: 10_000,
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          },
        );
      },
    },
  );

  const suiteTestsCols = useMemo(() => {
    // Use Set for O(1) lookups instead of Array.includes() (React best practice)
    const explicitTestIdSet = new Set(suite?.explicit_test_ids ?? []);

    // Helper to determine why a test is in the suite
    const getTestSource = (test: Test) => {
      const isExplicit = explicitTestIdSet.has(test.id);
      const matchingTags =
        suite?.tag_filters?.filter((tagFilter) => test.tags?.includes(tagFilter)) ?? [];
      const isTagMatched = matchingTags.length > 0;

      if (isExplicit && isTagMatched) {
        return { type: "both" as const, tags: matchingTags };
      } else if (isExplicit) {
        return { type: "manual" as const, tags: [] };
      } else if (isTagMatched) {
        return { type: "tag" as const, tags: matchingTags };
      }
      return { type: "unknown" as const, tags: [] };
    };

    // Add membership column to show why test is in the suite
    const membershipColumn = {
      field: "membership",
      headerName: "Membership",
      width: 140,
      renderCell: (params: GridRenderCellParams<Test>) => {
        const source = getTestSource(params.row);
        if (source.type === "manual") {
          return (
            <Chip
              label="Manual"
              size="small"
              sx={{
                fontSize: "0.7rem",
                height: 22,
                backgroundColor: STATUS_COLORS.success.bg,
                color: STATUS_COLORS.success.text,
              }}
            />
          );
        } else if (source.type === "tag") {
          return (
            <Tooltip title={`Matched tags: ${source.tags.join(", ")}`}>
              <Chip
                label={`Tag: ${source.tags[0]}${source.tags.length > 1 ? ` +${source.tags.length - 1}` : ""}`}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  height: 22,
                  backgroundColor: STATUS_COLORS.info.bg,
                  color: STATUS_COLORS.info.text,
                }}
              />
            </Tooltip>
          );
        } else if (source.type === "both") {
          return (
            <Tooltip title={`Manual + Tags: ${source.tags.join(", ")}`}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
                <Chip
                  label="Manual"
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    height: 20,
                    backgroundColor: STATUS_COLORS.success.bg,
                    color: STATUS_COLORS.success.text,
                  }}
                />
                <Chip
                  label={`+${source.tags.length} tag${source.tags.length > 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    height: 20,
                    backgroundColor: STATUS_COLORS.info.bg,
                    color: STATUS_COLORS.info.text,
                  }}
                />
              </Stack>
            </Tooltip>
          );
        }
        return null;
      },
    };

    const columns = testColumns.map((column) => {
      if (column.field === "actions") {
        return {
          ...column,
          renderCell: (params: GridRenderCellParams<Test>) =>
            column.renderCell?.({
              ...params,
              onDeleteTest: () => {
                refetchSuites();
              },
            } as TestActionColumnProps),
        };
      }
      return column;
    });

    // Insert membership column after name column
    const nameIndex = columns.findIndex((col) => col.field === "name");
    if (nameIndex !== -1) {
      columns.splice(nameIndex + 1, 0, membershipColumn);
    } else {
      columns.unshift(membershipColumn);
    }

    return columns;
  }, [refetchSuites, suite?.explicit_test_ids, suite?.tag_filters]);

  return (
    <Box
      sx={{
        flex: 1,
        flexDirection: "column",
        display: "flex",
        overflow: "hidden",
      }}
    >
      <PageHeader
        title={
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                letterSpacing: "-0.02em",
              }}
            >
              {suite?.name ?? "Test Suite Details"}
            </Typography>
            {suite?.tag_filters && suite.tag_filters.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                {suite.tag_filters.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      height: 18,
                      backgroundColor: STATUS_COLORS.info.bg,
                      borderColor: STATUS_COLORS.info.border,
                      color: STATUS_COLORS.info.text,
                      "& .MuiChip-label": {
                        px: 0.5,
                      },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        }
        subtitle={suite?.description ?? "Manage your test suite configuration and runs"}
        actions={
          <>
            <Tooltip
              title="Test run rules to be applied for the tests of this suite"
              placement="top"
            >
              <IconButton
                size="small"
                onClick={() => setAddRuleOpen(true)}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  border: 0,
                  borderStyle: "solid",
                  borderColor: "grey.300",
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "warning.main",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View CI Configurations" placement="top">
              <IconButton
                size="small"
                onClick={() => setCiOpen(true)}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  border: 0,
                  borderStyle: "solid",
                  borderColor: "grey.300",
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add or remove tests from this suite" placement="top">
              <IconButton
                size="small"
                onClick={() => setAddTestOpen(true)}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  border: 0,
                  borderStyle: "solid",
                  borderColor: "grey.300",
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Trigger a new test run for this suite" placement="top">
              <IconButton
                size="small"
                disabled={isRunningSuite}
                onClick={() => {
                  runSuite({});
                }}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  border: 0,
                  borderStyle: "solid",
                  borderColor: "grey.300",
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "common.white",
                  },
                  "&:disabled": {
                    borderColor: "grey.300",
                    backgroundColor: "grey.100",
                    color: "grey.500",
                  },
                }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      <Dialog
        open={addRuleOpen}
        onClose={() => setAddRuleOpen(false)}
        aria-labelledby="run-rules-dialog-title"
        aria-describedby="run-rules-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="run-rules-dialog-title">
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Test Run Rules</Typography>
            <IconButton
              size="small"
              onClick={() => {
                setAddRuleOpen(false);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={2} mb={2}>
            <Typography variant="body1">
              Define test run rules for this test suite, e.g. if you want a specific test to run
              before another one, independetly of automatic test resolution.
            </Typography>
            <Alert severity="info">
              This can be useful for when you are using a test as a "seed" for the whole suite, so
              you want it to run before any other test.
            </Alert>
            <Divider />
            {Object.entries(suite?.test_dependency_graph_conditions ?? {}).length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No conditions defined for this test suite.
              </Typography>
            )}
            <Stack direction="column" spacing={1} alignItems="flex-start">
              {Object.entries(suite?.test_dependency_graph_conditions ?? {}).map(
                ([testId, testDeps]) => (
                  <Stack
                    direction="row"
                    spacing={1}
                    key={testId}
                    width="100%"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1">
                        {tests?.find((t) => t.id === testId)?.name ?? "Unknown Test"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        before
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        divider={
                          <Typography variant="body2" color="textSecondary">
                            &
                          </Typography>
                        }
                        alignItems="center"
                        justifyContent="center"
                      >
                        {testDeps.map((dep) => (
                          <Typography key={dep} variant="body1">
                            {tests?.find((t) => t.id === dep)?.name ?? "Unknown Dependency"}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={isUpdatingSuite}
                      loading={isUpdatingSuite}
                      onClick={() => {
                        if (!suite) return;

                        const newConditions = {
                          ...suite?.test_dependency_graph_conditions,
                        };
                        delete newConditions[testId];

                        updateSuite({
                          ...suite,
                          test_dependency_graph_conditions: newConditions,
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                ),
              )}
            </Stack>
            <Divider />
            <Stack direction="row" spacing={1}>
              <Autocomplete
                options={(tests ?? []).filter((t) => suite?.test_ids.includes(t.id))}
                getOptionLabel={(option) => option.name}
                value={rule.left ? tests?.find((t) => t.id === rule.left) : null}
                onChange={(_, newValue) => {
                  setRule({
                    left: newValue ? newValue.id : null,
                    right: rule.right,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select test to run before others" size="small" />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
              />
              <Autocomplete
                options={(tests ?? []).filter((t) => suite?.test_ids.includes(t.id))}
                getOptionLabel={(option) => option.name}
                value={rule.right ? tests?.find((t) => t.id === rule.right) : null}
                onChange={(_, newValue) => {
                  setRule({
                    left: rule.left,
                    right: newValue ? newValue.id : null,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select test to run after" size="small" />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
              />
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={
                  !suite || !rule.left || !rule.right || rule.left === rule.right || isUpdatingSuite
                }
                loading={isUpdatingSuite}
                onClick={() => {
                  if (!suite || !rule.left || !rule.right) {
                    return;
                  }

                  const newConditions = {
                    ...suite?.test_dependency_graph_conditions,
                    [rule.left]: [
                      ...(suite?.test_dependency_graph_conditions?.[rule.left] ?? []),
                      rule.right,
                    ],
                  };

                  updateSuite({
                    ...suite,
                    test_dependency_graph_conditions: newConditions,
                  });
                }}
                sx={{
                  width: "40px",
                }}
              >
                Add
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        open={ciOpen}
        onClose={() => setCiOpen(false)}
        aria-labelledby="ci-code-dialog-title"
        aria-describedby="ci-code-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="ci-code-dialog-title">CI Configuration</DialogTitle>
        <DialogContent>
          <DialogContentText id="ci-code-dialog-description">
            <Stack direction="column" spacing={2} mb={2}>
              <Typography variant="subtitle1">Github Configuration</Typography>
              <Typography variant="body2">
                Copy this YAML to your GitHub Action workflow file to run this test suite every time
                the workflow is triggered.
              </Typography>
              <Editor
                height="120px"
                width="100%"
                theme="vs-light"
                defaultLanguage="yaml"
                defaultValue={ghCode}
                options={{
                  readOnly: true,
                  minimap: {
                    enabled: false,
                  },
                  scrollBeyondLastLine: false,
                }}
              />
            </Stack>
            <Stack direction="column" spacing={2} mb={2}>
              <Typography variant="subtitle1">Gitlab Configuration</Typography>
              <Typography variant="body2">
                Copy this YAML to your Gitlab CI/CD workflow file to run this test suite every time
                the workflow is triggered.
              </Typography>
              <Editor
                height="180px"
                width="100%"
                theme="vs-light"
                defaultLanguage="yaml"
                defaultValue={glCode}
                options={{
                  readOnly: true,
                  minimap: {
                    enabled: false,
                  },
                  scrollBeyondLastLine: false,
                }}
              />
            </Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(ghCode);

              enqueueSnackbar("Github YAML copied to clipboard", {
                variant: "success",
                anchorOrigin: {
                  horizontal: "right",
                  vertical: "bottom",
                },
              });

              setCiOpen(false);
            }}
            autoFocus
          >
            Copy Github YAML
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(glCode);

              enqueueSnackbar("Gitlab YAML copied to clipboard", {
                variant: "success",
                anchorOrigin: {
                  horizontal: "right",
                  vertical: "bottom",
                },
              });

              setCiOpen(false);
            }}
            autoFocus
          >
            Copy Gitlab YAML
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addTestOpen} onClose={() => setAddTestOpen(false)} fullWidth maxWidth="md">
        <DialogContent>
          <TestSplitSelect
            title="Select tests to add or remove from this suite"
            value={suiteTests.map((test) => test.id)}
            onChange={(ids) => {
              const selectedTests = tests?.filter((test) => ids.includes(test.id));
              setSuiteTests(selectedTests ?? []);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setAddTestOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={isUpdatingSuite}
            loading={isUpdatingSuite}
            onClick={() => {
              if (!suite) return;

              updateSuite({
                ...suite,
                test_ids: suiteTests.map((test) => test.id),
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleChange} aria-label="Test details tabs">
            <Tab label="Configuration" value="details" />
            <Tab label="Tests" value="tests" />
            <Tab label="Graph" value="graph" />
            <Tab label="Runs" value="runs" />
          </TabList>
        </Box>
        <TabPanel value="details">
          {detailsData && (
            <Box sx={{ p: 3 }}>
              <Stack direction="column" spacing={4}>
                {/* Basic Information Section */}
                <Box>
                  <SectionHeader>Basic Information</SectionHeader>
                  <Stack direction="column" spacing={3}>
                    <TextField
                      autoFocus
                      required
                      id="name"
                      name="name"
                      label="Suite Name"
                      placeholder="Daily acceptance tests"
                      size="small"
                      type="text"
                      fullWidth
                      value={detailsData.name}
                      onChange={handleDetailsChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />

                    <TextField
                      required={false}
                      id="description"
                      name="description"
                      label="Description"
                      placeholder="E.g. daily acceptance tests after deploy to staging..."
                      size="small"
                      multiline
                      minRows={3}
                      maxRows={5}
                      type="text"
                      fullWidth
                      value={detailsData.description || ""}
                      onChange={handleDetailsChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Stack>
                </Box>

                {/* Scheduling Section */}
                <Box>
                  <SectionHeader>Scheduling</SectionHeader>
                  <Stack direction="column" spacing={3}>
                    <Grid container spacing={{ xs: 2 }}>
                      <Grid size={{ xs: 6, md: 9 }}>
                        <TextField
                          required={false}
                          id="schedule"
                          name="schedule"
                          label="Schedule CRON"
                          placeholder="E.g. 0 0 * * * for daily"
                          size="small"
                          type="text"
                          fullWidth
                          error={
                            !!detailsData.schedule &&
                            detailsData.schedule !== "" &&
                            !cron(detailsData.schedule).isValid()
                          }
                          helperText={
                            !!detailsData.schedule &&
                            detailsData.schedule !== "" &&
                            !cron(detailsData.schedule).isValid()
                              ? "Invalid CRON schedule"
                              : "Leave empty to run manually"
                          }
                          value={detailsData.schedule || ""}
                          onChange={handleDetailsChange}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Select
                          fullWidth
                          size="small"
                          value={detailsData.schedule_enabled ? "on" : "off"}
                          onChange={handleScheduleEnabledChange}
                          sx={{
                            borderRadius: 2,
                          }}
                        >
                          <MenuItem value="on">CRON Enabled</MenuItem>
                          <MenuItem value="off">CRON Disabled</MenuItem>
                        </Select>
                      </Grid>
                    </Grid>

                    <TextField
                      required={false}
                      id="ai-schedule"
                      name="ai-schedule"
                      label="AI-Powered Schedule Generation"
                      placeholder="E.g. Every day at midnight"
                      size="small"
                      type="text"
                      fullWidth
                      helperText="Don't know how to write a CRON? Describe your schedule and press Enter"
                      disabled={isAiProcessing}
                      value={aiText}
                      onChange={(e) => setAiText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          ai({
                            query: `Generate a valid CRON expression for the following recurrence "${aiText}"`,
                            output_model: {
                              type: "string",
                              name: "cron",
                              description: "Just the VALID CRON expression, no explanation",
                            },
                          });
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Stack>
                </Box>

                {/* Notification Settings Section */}
                <Box>
                  <SectionHeader>Notification Settings</SectionHeader>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Override the default Slack notification channel for this test suite. Leave empty
                    to use the organization's default channel.
                  </Typography>
                  <TextField
                    id="slack_channel_override"
                    name="slack_channel_override"
                    label="Slack Channel Override"
                    placeholder="#suite-alerts or C01234567"
                    size="small"
                    type="text"
                    fullWidth
                    helperText="Channel name (e.g., #channel-name) or channel ID. Overrides default notification channel."
                    value={detailsData.slack_channel_override || ""}
                    onChange={handleDetailsChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>

                {/* Tag Filters Section */}
                <Box>
                  <SectionHeader>Tag-Based Test Filtering</SectionHeader>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Select tags to dynamically include tests that have these tags. This allows you
                    to run tests based on their tags rather than explicitly selecting each test.
                    Tests with any of the selected tags will be automatically included in this
                    suite.
                  </Typography>

                  <Autocomplete
                    multiple
                    size="small"
                    options={allSuggestedTags}
                    value={detailsData.tag_filters || []}
                    onChange={(_, value) => {
                      setDetailsData((prev) =>
                        prev
                          ? {
                              ...prev,
                              tag_filters: value,
                            }
                          : null,
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select tags to filter tests..."
                        size="small"
                        helperText="Tests with any of these tags will be included in this suite"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const style = getTagStyle(option);
                        return (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={option}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              height: 22,
                              backgroundColor: style.color,
                              borderColor: style.borderColor,
                              color: style.textColor,
                              borderRadius: 1.5,
                              "& .MuiChip-label": {
                                px: 1,
                              },
                              "& .MuiChip-deleteIcon": {
                                color: style.textColor,
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        );
                      })
                    }
                    renderOption={(props, option) => {
                      const style = getTagStyle(option);
                      return (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            fontSize: "0.875rem",
                            "&:hover": {
                              backgroundColor: style.color,
                            },
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            py: 0.5,
                          }}
                        >
                          <Chip
                            label={option}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.75rem",
                              height: 20,
                              mr: 1,
                              backgroundColor: style.color,
                              borderColor: style.borderColor,
                              color: style.textColor,
                              borderRadius: 1.5,
                            }}
                          />
                          {option}
                        </Box>
                      );
                    }}
                    freeSolo
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    clearOnBlur
                    handleHomeEndKeys
                  />
                </Box>

                <Box>
                  <SectionHeader>Run Settings</SectionHeader>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Control how the tests in this suite are executed, including failure handling and
                    concurrency.
                  </Typography>
                  <Grid container spacing={{ xs: 2 }}>
                    <Grid size={{ xs: 6 }}>
                      <Autocomplete
                        size="small"
                        options={TEST_SUITE_FAILURE_STRATEGY_LIST}
                        value={detailsData.failure_strategy}
                        onChange={(_, strat) =>
                          setDetailsData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  failure_strategy: strat,
                                }
                              : null,
                          )
                        }
                        renderOption={(props, option) => (
                          <Typography {...props} variant="body2">
                            {humanizeText(option)}
                          </Typography>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select failure strategy..."
                            label="Failure Strategy"
                            size="small"
                          />
                        )}
                        getOptionLabel={(option) => humanizeText(option)}
                        autoComplete
                        includeInputInList
                        filterSelectedOptions
                        clearOnBlur
                        handleHomeEndKeys
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        required={false}
                        id="concurrency"
                        name="concurrency"
                        label="Max Concurrency"
                        placeholder="E.g. 3"
                        size="small"
                        type="number"
                        fullWidth
                        InputProps={{ inputProps: { min: 1, max: 10 } }}
                        helperText="Number of tests to run in parallel when running this suite"
                        value={detailsData.max_concurrent_runs}
                        onChange={(e) =>
                          setDetailsData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  max_concurrent_runs: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                }
                              : null,
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Reset to original data
                      if (suite) {
                        setDetailsData({
                          name: suite.name,
                          description: suite.description,
                          schedule: suite.schedule,
                          schedule_enabled: suite.schedule_enabled,
                          test_ids: suite.test_ids,
                          webhook_ids: suite.webhook_ids || [],
                          slack_channel_override: suite.slack_channel_override,
                          tag_filters: suite.tag_filters || [],
                          test_dependency_graph: suite.test_dependency_graph || {},
                          test_dependency_graph_conditions:
                            suite.test_dependency_graph_conditions || {},
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                    }}
                  >
                    Reset Changes
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!detailsData?.name || isUpdatingSuite}
                    loading={isUpdatingSuite}
                    onClick={handleSaveDetails}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </TabPanel>
        <TabPanel value="tests">
          <DataGrid loading={isLoadingSuite} rows={suite?.tests ?? []} columns={suiteTestsCols} />
        </TabPanel>
        <TabPanel value="graph">
          <GraphVisualization nodes={graph.nodes} edges={graph.edges} horizontal={true} />
        </TabPanel>
        <TabPanel value="runs">
          <DataGrid
            loading={isLoadingSuiteRuns}
            rows={suiteRuns ?? []}
            columns={suiteRunColumns}
            pageSizeOptions={[25]}
            onRowClick={(params) => {
              nav(`/suite-runs/${params.row.id}`);
            }}
            customSx={{
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
